import { useTweaks } from '../components/Tweaks.jsx';
import { Header } from '../components/Header.jsx';
import { Footer } from '../components/Blocks.jsx';
import { GiftHero, GiftConfigurator, GiftHowItWorks, GiftFaq } from '../components/GiftPage.jsx';

const TWEAK_DEFAULTS = {
  navWelshResources: 'Welsh Resources',
  navAboutUs: 'About us',
  navContact: 'Contact',
  startLearning: 'Start Learning',

  giftPageKicker: 'Gift voucher',
  giftPageTitle: 'Give someone a language.',
  giftPageLede: "It's an odd thing to gift-wrap, a language – but it's one of the few presents people still talk about years later. One voucher unlocks all 11 of our courses, so the recipient picks where they start. You choose the length, write a proper note, and we deliver the voucher on the day that suits.",
  giftPageBullet1: 'All 11 languages included · full access to the SSi method',
  giftPageBullet2: 'Delivered by email – no card in the post required',
  giftPageBullet3: "Never expires · recipient can start when they're ready",

  giftConfigKicker: 'Build it',
  giftConfigTitle: 'Design their voucher.',
  giftStep1: 'Choose a length',
  giftStep2: 'Choose a primary language',
  giftStep3: "Who's it for?",
  giftStep4: 'When should it arrive?',

  giftPopularLabel: 'Most gifted',
  giftLangNote: 'This is just the language that appears on the voucher. The recipient gets full access to all 11 courses and can switch between them any time.',

  giftFieldRecipient: "Recipient's name",
  giftFieldRecipientPh: 'Nain',
  giftFieldSender: 'Your name',
  giftFieldSenderPh: 'Catrin',
  giftFieldMessage: 'Your message',
  giftFieldMessagePh: 'A sentence or two. No need for a speech.',

  giftDefaultRecipient: 'your person',
  giftDefaultSender: 'you',
  giftDefaultMessage: "Happy birthday, Nain. Let's have that conversation in Welsh this Christmas – diolch am bopeth.",

  giftDeliveryNow: 'Send it to me now',
  giftDeliveryNowSub: 'You forward it whenever – printable PDF included.',
  giftDeliveryLater: 'Schedule the email',
  giftDeliveryLaterSub: "We'll send it straight to them on the date you pick.",

  giftPrintOption: 'Post me a printed card to sign and pass on',

  giftTotalLabel: 'Total today',
  giftTotalFine: 'Never expires. Full refund for 14 days if unredeemed.',
  giftCheckoutCta: 'Checkout',

  giftPreviewLabel: 'Live preview',
  giftPreviewNote: "This is exactly what they'll see – your message, your names, their voucher code.",

  giftHowKicker: 'How it works',
  giftHowTitle: 'Three steps, no printer required.',
  giftHowStep1Title: 'You build it',
  giftHowStep1Body: "Pick a length, choose a primary language, write a note. See the voucher update as you type – no 'generate preview' button.",
  giftHowStep2Title: 'We deliver it',
  giftHowStep2Body: 'Either straight to you to forward on, or scheduled to land in their inbox at 7am on the morning you pick.',
  giftHowStep3Title: 'They get going',
  giftHowStep3Body: "They click a link, create their account, and they're five minutes away from speaking their first sentence.",

  giftFaqKicker: 'Questions',
  giftFaqTitle: 'The things people ask us.',
  giftFaqQ1: 'What if they already have an SSi account?',
  giftFaqA1: "No problem. The voucher extends their existing subscription or reactivates a lapsed one. If they're mid-course, they keep their progress.",
  giftFaqQ2: 'Does the voucher expire?',
  giftFaqA2: "Never. Once purchased, the code is valid forever. If they redeem it and don't use the service for a while, their subscription pauses – it doesn't burn through days they aren't using.",
  giftFaqQ3: 'What if they want to learn a different language to the one I picked?',
  giftFaqA3: "That's fine – the voucher unlocks all 11 of our courses. The language on the voucher is just a starting suggestion; once they redeem it they can pick whichever course they like, and switch between them whenever they want.",
  giftFaqQ4: 'Can I buy multiple vouchers at once?',
  giftFaqA4: "Yes – go through the flow once, and at checkout there's a 'buy another' button that keeps your details and just resets the recipient. We do bulk orders for schools and workplaces too – drop us a line.",
  giftFaqQ5: 'Can I gift it to myself?',
  giftFaqA5: "We're not going to stop you. People often do as a birthday or new-year nudge. Just put your own name in both boxes.",

  footerCta: 'Start your language learning journey today.',
  footerCol1: 'What we do',
  footerCol2: 'SaySomethingin Learners',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

const GROUPS = [
  { name: 'Header', keys: ['navWelshResources','navAboutUs','navContact','startLearning'] },
  { name: 'Hero', keys: ['giftPageKicker','giftPageTitle','giftPageLede','giftPageBullet1','giftPageBullet2','giftPageBullet3'], long:['giftPageLede'] },
  { name: 'Configurator – labels', keys: ['giftConfigKicker','giftConfigTitle','giftStep1','giftStep2','giftStep3','giftStep4','giftPopularLabel','giftLangNote'], long:['giftLangNote'] },
  { name: 'Configurator – fields', keys: ['giftFieldRecipient','giftFieldRecipientPh','giftFieldSender','giftFieldSenderPh','giftFieldMessage','giftFieldMessagePh','giftDefaultRecipient','giftDefaultSender','giftDefaultMessage'], long:['giftDefaultMessage','giftFieldMessagePh'] },
  { name: 'Configurator – delivery & total', keys: ['giftDeliveryNow','giftDeliveryNowSub','giftDeliveryLater','giftDeliveryLaterSub','giftPrintOption','giftTotalLabel','giftTotalFine','giftCheckoutCta'], long:['giftDeliveryNowSub','giftDeliveryLaterSub','giftTotalFine'] },
  { name: 'Preview', keys: ['giftPreviewLabel','giftPreviewNote'], long:['giftPreviewNote'] },
  { name: 'How it works', keys: ['giftHowKicker','giftHowTitle','giftHowStep1Title','giftHowStep1Body','giftHowStep2Title','giftHowStep2Body','giftHowStep3Title','giftHowStep3Body'], long:['giftHowStep1Body','giftHowStep2Body','giftHowStep3Body'] },
  { name: 'FAQ', keys: ['giftFaqKicker','giftFaqTitle','giftFaqQ1','giftFaqA1','giftFaqQ2','giftFaqA2','giftFaqQ3','giftFaqA3','giftFaqQ4','giftFaqA4','giftFaqQ5','giftFaqA5'], long:['giftFaqA1','giftFaqA2','giftFaqA3','giftFaqA4','giftFaqA5'] },
  { name: 'Footer', keys: ['footerCta','footerCol1','footerCol2','footerLegal'], long:['footerLegal'] },
];

export default function Gift() {
  const { tweaks, panel } = useTweaks({ defaults: TWEAK_DEFAULTS, groups: GROUPS });
  return (
    <div data-screen-label="Gift voucher">
      <Header t={tweaks}/>
      <main>
        <GiftHero t={tweaks}/>
        <GiftConfigurator t={tweaks}/>
        <GiftHowItWorks t={tweaks}/>
        <GiftFaq t={tweaks}/>
      </main>
      <Footer t={tweaks} hideCta={true}/>
      {panel}
    </div>
  );
}
